/**
 * Acompanhamento da emissão na TELA (o envio e as consultas ao provedor são do worker, no
 * servidor, e continuam com a página fechada). Aqui só se lê a situação gravada no banco:
 * nenhuma função deste arquivo fala com o provedor ou com a SEFAZ.
 *
 *  - `emissionStage`: em que ponto está a nota, em linguagem de quem vende — a partir da
 *    situação do documento e da fila do worker (sem inventar estado);
 *  - `pollDelay`: de quanto em quanto tempo a TELA relê a situação (no próprio sistema),
 *    mais rápido no início e espaçando depois;
 *  - `fiscalFileName`: nomes dos arquivos baixados (série, número, homologação);
 *  - `makeZip`: PDF + XML num único arquivo, quando o navegador barra vários downloads.
 */
import type { FiscalDocumentDetail, FiscalDocumentStatus } from "@cafegestor/shared";

export type EmissionStageKey =
  | "preparando"
  | "sem_worker"
  | "enviando"
  | "aguardando"
  | "falha_comunicacao"
  | "nova_tentativa"
  | "parada"
  | "contingencia"
  | "autorizada"
  | "rejeitada"
  | "denegada"
  | "cancelada"
  | "encerrada";

export type EmissionStage = {
  key: EmissionStageKey;
  tone: "info" | "warn" | "ok" | "error" | "neutral";
  /** título curto (ex.: "NF-e autorizada pela SEFAZ.") */
  title: string;
  /** o que está acontecendo e o que fazer (sem termos técnicos) */
  detail: string;
  /** ainda vai mudar sozinha (a tela continua relendo) */
  waiting: boolean;
};

/** Situações em que a nota ainda vai mudar sem ninguém fazer nada. */
export const WAITING_STATUSES: readonly FiscalDocumentStatus[] = [
  "pendente",
  "processando",
  "incerta",
  "contingencia",
];

/** Sem nenhuma tentativa depois deste tempo, o worker provavelmente não está rodando. */
export const NO_WORKER_AFTER_MS = 60_000;

const hhmm = (iso: string) =>
  new Date(iso).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });

type DocLike = Pick<
  FiscalDocumentDetail,
  "status" | "model" | "sefazCode" | "sefazMessage" | "statusReason" | "requestedAt" | "job"
> & { attempts?: readonly unknown[] };

export function emissionStage(d: DocLike, now: number = Date.now()): EmissionStage {
  const nfe = d.model === "65" ? "NFC-e" : "NF-e";
  const job = d.job;
  const tried = (d.attempts?.length ?? 0) > 0 || (job?.attempts ?? 0) > 0;
  switch (d.status) {
    case "autorizada":
      return {
        key: "autorizada",
        tone: "ok",
        title: `${nfe} autorizada pela SEFAZ.`,
        detail: "O DANFE e o XML autorizado (com o protocolo) estão disponíveis.",
        waiting: false,
      };
    case "rejeitada":
      return {
        key: "rejeitada",
        tone: "error",
        title: `${nfe} rejeitada${d.sefazCode ? ` (código ${d.sefazCode})` : ""}.`,
        detail: `${d.sefazMessage ?? d.statusReason ?? "A SEFAZ não informou o motivo."} A venda continua confirmada (estoque e financeiro não se repetem). Corrija o que a SEFAZ apontou e use "Recalcular (prévia do reenvio)": a nota sai com o mesmo número.`,
        waiting: false,
      };
    case "denegada":
      return {
        key: "denegada",
        tone: "error",
        title: `${nfe} denegada pela SEFAZ.`,
        detail: `${d.sefazMessage ?? "Uso denegado."} O número fica usado e esta venda não pode seguir com nota: estorne a venda em Vendas → Pedidos de venda.`,
        waiting: false,
      };
    case "cancelada":
      return {
        key: "cancelada",
        tone: "neutral",
        title: `${nfe} cancelada.`,
        detail: "O cancelamento foi registrado na SEFAZ.",
        waiting: false,
      };
    case "abandonada":
    case "inutilizada":
      return {
        key: "encerrada",
        tone: "neutral",
        title: d.status === "abandonada" ? `${nfe} abandonada.` : "Número inutilizado.",
        detail: "Esta nota não será enviada.",
        waiting: false,
      };
    case "contingencia":
      return {
        key: "contingencia",
        tone: "warn",
        title: `${nfe} emitida em contingência.`,
        detail: "O sistema transmite à SEFAZ assim que possível e atualiza a situação aqui.",
        waiting: true,
      };
    default:
      break;
  }
  // a fila parou (ex.: o provedor recusou o acesso): o conteúdo e o número continuam os mesmos
  if (job?.status === "falhou") {
    return {
      key: "parada",
      tone: "error",
      title: "A emissão parou e precisa de atenção.",
      detail: `${job.lastError ?? "O serviço de emissão não conseguiu concluir."} Confira a configuração fiscal e use "Consultar agora": a nota segue com o mesmo número; nenhuma outra é criada.`,
      waiting: false,
    };
  }
  if (d.status === "incerta") {
    const next =
      job?.status === "pendente" && Date.parse(job.nextRunAt) > now ? job.nextRunAt : null;
    const later = next !== null;
    return {
      key: later ? "nova_tentativa" : "falha_comunicacao",
      tone: "warn",
      title: later
        ? "Falha de comunicação. Aguardando nova tentativa."
        : "Falha de comunicação com o provedor. Confirmando a situação…",
      detail: `O provedor não respondeu a tempo. Antes de qualquer novo envio, o sistema CONSULTA a situação da nota${
        next ? ` (próxima consulta às ${hhmm(next)})` : ""
      } — nenhuma nota é duplicada. Não emita outra nota para esta venda.`,
      waiting: true,
    };
  }
  if (job?.status === "executando") {
    return d.status === "pendente"
      ? {
          key: "enviando",
          tone: "info",
          title: "Enviando à SEFAZ…",
          detail: "A nota está sendo transmitida pelo provedor fiscal.",
          waiting: true,
        }
      : {
          key: "aguardando",
          tone: "info",
          title: `${nfe} enviada. Consultando a SEFAZ…`,
          detail: "O sistema está consultando o resultado da autorização.",
          waiting: true,
        };
  }
  if (d.status === "processando") {
    return {
      key: "aguardando",
      tone: "info",
      title: `${nfe} enviada. Aguardando autorização da SEFAZ.`,
      detail: `O sistema consulta o resultado sozinho${
        job?.status === "pendente" && Date.parse(job.nextRunAt) > now
          ? ` (próxima consulta às ${hhmm(job.nextRunAt)})`
          : ""
      }. Pode continuar trabalhando: a consulta segue mesmo com esta página fechada.`,
      waiting: true,
    };
  }
  // pendente: na fila do worker
  if (!tried && now - Date.parse(d.requestedAt) > NO_WORKER_AFTER_MS) {
    return {
      key: "sem_worker",
      tone: "warn",
      title: "A nota ainda não foi enviada.",
      detail:
        "Está na fila há mais de 1 minuto sem nenhuma tentativa: confira se o serviço de emissão (worker fiscal) está rodando. Assim que ele estiver ativo, a nota é enviada sozinha — não emita outra.",
      waiting: true,
    };
  }
  return {
    key: "preparando",
    tone: "info",
    title: "Preparando a emissão…",
    detail: "A nota está na fila de envio à SEFAZ.",
    waiting: true,
  };
}

/**
 * Intervalo entre as leituras da TELA (no banco do sistema, não no provedor): 3 s no primeiro
 * minuto, 5 s até 3 min, 15 s até 15 min e 30 s depois disso. `false` = parar.
 */
export function pollDelay(elapsedMs: number, waiting: boolean): number | false {
  if (!waiting) return false;
  if (elapsedMs < 60_000) return 3_000;
  if (elapsedMs < 180_000) return 5_000;
  if (elapsedMs < 900_000) return 15_000;
  return 30_000;
}

/**
 * Nomes dos arquivos: `NFe_serie2_numero3_DANFE.pdf` e `NFe_serie2_numero3_autorizada.xml`.
 * Fora da produção, o nome diz isso (nunca confundir com documento com valor fiscal).
 */
export function fiscalFileName(
  d: Pick<FiscalDocumentDetail, "model" | "series" | "number" | "environment">,
  kind: "danfe_pdf" | "danfe_html" | "xml" | "zip",
): string {
  const base = `${d.model === "65" ? "NFCe" : "NFe"}_serie${d.series}_numero${d.number}`;
  const env = d.environment === "producao" ? "" : `_${d.environment}`;
  switch (kind) {
    case "danfe_pdf":
      return `${base}_DANFE${env}.pdf`;
    case "danfe_html":
      return `${base}_DANFE${env}.html`;
    case "xml":
      return `${base}_autorizada${env}.xml`;
    default:
      return `${base}${env}.zip`;
  }
}

// ------------------------------------------------------------------ ZIP (sem compressão)
const CRC_TABLE = (() => {
  const t = new Uint32Array(256);
  for (let n = 0; n < 256; n++) {
    let c = n;
    for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    t[n] = c >>> 0;
  }
  return t;
})();

export function crc32(data: Uint8Array): number {
  let c = 0xffffffff;
  for (const byte of data) c = CRC_TABLE[(c ^ byte) & 0xff]! ^ (c >>> 8);
  return (c ^ 0xffffffff) >>> 0;
}

/**
 * Arquivo ZIP simples (método "store", sem compressão) com os arquivos informados. PDF e XML
 * já são pequenos; assim não é preciso biblioteca nova. Datas no formato DOS (fixas no dia).
 */
export function makeZip(
  files: { name: string; data: Uint8Array }[],
  date: Date = new Date(),
): Uint8Array<ArrayBuffer> {
  const enc = new TextEncoder();
  const dosTime = (date.getHours() << 11) | (date.getMinutes() << 5) | (date.getSeconds() >> 1);
  const dosDate =
    ((date.getFullYear() - 1980) << 9) | ((date.getMonth() + 1) << 5) | date.getDate();
  const locals: Uint8Array[] = [];
  const centrals: Uint8Array[] = [];
  let offset = 0;
  for (const f of files) {
    const name = enc.encode(f.name);
    const crc = crc32(f.data);
    const local = new DataView(new ArrayBuffer(30));
    local.setUint32(0, 0x04034b50, true);
    local.setUint16(4, 20, true); // versão
    local.setUint16(6, 0x0800, true); // nome em UTF-8
    local.setUint16(8, 0, true); // store
    local.setUint16(10, dosTime, true);
    local.setUint16(12, dosDate, true);
    local.setUint32(14, crc, true);
    local.setUint32(18, f.data.length, true);
    local.setUint32(22, f.data.length, true);
    local.setUint16(26, name.length, true);
    local.setUint16(28, 0, true);
    locals.push(new Uint8Array(local.buffer), name, f.data);
    const central = new DataView(new ArrayBuffer(46));
    central.setUint32(0, 0x02014b50, true);
    central.setUint16(4, 20, true);
    central.setUint16(6, 20, true);
    central.setUint16(8, 0x0800, true);
    central.setUint16(10, 0, true);
    central.setUint16(12, dosTime, true);
    central.setUint16(14, dosDate, true);
    central.setUint32(16, crc, true);
    central.setUint32(20, f.data.length, true);
    central.setUint32(24, f.data.length, true);
    central.setUint16(28, name.length, true);
    central.setUint32(42, offset, true);
    centrals.push(new Uint8Array(central.buffer), name);
    offset += 30 + name.length + f.data.length;
  }
  const centralSize = centrals.reduce((n, c) => n + c.length, 0);
  const end = new DataView(new ArrayBuffer(22));
  end.setUint32(0, 0x06054b50, true);
  end.setUint16(8, files.length, true);
  end.setUint16(10, files.length, true);
  end.setUint32(12, centralSize, true);
  end.setUint32(16, offset, true);
  const parts = [...locals, ...centrals, new Uint8Array(end.buffer)];
  const out = new Uint8Array(parts.reduce((n, p) => n + p.length, 0));
  let at = 0;
  for (const p of parts) {
    out.set(p, at);
    at += p.length;
  }
  return out;
}

// ------------------------------------------------------------------ linha do tempo
export type TimelineState = "done" | "current" | "pending" | "warn" | "error";
export type TimelineStep = { key: string; label: string; state: TimelineState };

const STEP_LABELS = [
  ["venda", "Venda confirmada"],
  ["solicitada", "Emissão solicitada"],
  ["enviada", "NF-e enviada para processamento"],
  ["aguardando", "Aguardando autorização da SEFAZ"],
  ["autorizada", "NF-e autorizada"],
  ["danfe", "DANFE disponível"],
] as const;

/**
 * Etapas REAIS da emissão (sem porcentagem inventada), deduzidas do estágio — que vem só da
 * situação gravada pelo worker. `filesReady`: DANFE e XML já guardados no sistema.
 */
export function emissionTimeline(
  stage: Pick<EmissionStage, "key">,
  opts: { filesReady: boolean; model?: "55" | "65" } = { filesReady: false },
): TimelineStep[] {
  const nfe = opts.model === "65" ? "NFC-e" : "NF-e";
  // índice da etapa em andamento e o tom dela
  let at: number;
  let tone: TimelineState = "current";
  let label: string | null = null;
  switch (stage.key) {
    case "preparando":
      at = 1;
      break;
    case "sem_worker":
      at = 1;
      tone = "warn";
      break;
    case "parada":
      at = 2;
      tone = "error";
      label = "Emissão parada (precisa de atenção)";
      break;
    case "enviando":
      at = 2;
      break;
    case "aguardando":
    case "contingencia":
      at = 3;
      break;
    case "falha_comunicacao":
    case "nova_tentativa":
      at = 3;
      tone = "warn";
      label = "Situação a confirmar com a SEFAZ";
      break;
    case "autorizada":
      at = opts.filesReady ? 6 : 5;
      break;
    case "rejeitada":
      at = 4;
      tone = "error";
      label = `${nfe} rejeitada`;
      break;
    case "denegada":
      at = 4;
      tone = "error";
      label = `${nfe} denegada`;
      break;
    case "cancelada":
      // foi autorizada antes: o cancelamento aparece no lugar do DANFE
      at = 5;
      tone = "error";
      label = `${nfe} cancelada`;
      break;
    default:
      at = 4;
      tone = "error";
      label = `${nfe} encerrada sem autorização`;
  }
  return STEP_LABELS.map(([key, base], i) => {
    const text = base.replace("NF-e", nfe);
    if (i < at) return { key, label: text, state: "done" as const };
    if (i === at) return { key, label: label ?? text, state: tone };
    return { key, label: text, state: "pending" as const };
  });
}

/** Tempo decorrido legível: "45 s", "2 min 05 s", "1 h 03 min". */
export function elapsedLabel(ms: number): string {
  const s = Math.max(0, Math.floor(ms / 1000));
  if (s < 60) return `${s} s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m} min ${String(s % 60).padStart(2, "0")} s`;
  return `${Math.floor(m / 60)} h ${String(m % 60).padStart(2, "0")} min`;
}

// ------------------------------------------------------------------ conteúdo dos arquivos
/** O começo do arquivo é de um PDF ("%PDF-")? */
export function looksLikePdf(head: Uint8Array): boolean {
  const sig = [0x25, 0x50, 0x44, 0x46, 0x2d];
  return sig.every((b, i) => head[i] === b);
}

/** É XML (e não uma página de erro em HTML)? Aceita BOM e espaços no começo. */
export function looksLikeXml(head: string): boolean {
  const t = head.replace(/^\uFEFF/, "").trimStart();
  if (!t.startsWith("<")) return false;
  return !/^<(!doctype\s+html|html)\b/i.test(t);
}
